import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Helper function to normalize text for PDF rendering
 *
 * IMPORTANT: jsPDF's built-in fonts (Helvetica, Times, Courier) only support
 * WinAnsiEncoding (CP1252), which does NOT include Turkish or many other UTF-8 characters.
 *
 * This function normalizes special characters to their closest ASCII equivalents
 * to ensure readable output in the PDF.
 *
 * For true UTF-8 support, you would need to:
 * 1. Add a custom TrueType font (.ttf) that includes Turkish characters
 * 2. Convert it to base64 and load it with doc.addFileToVFS() and doc.addFont()
 * 3. Use doc.setFont() to apply it
 *
 * Example fonts with good UTF-8 support: Roboto, Open Sans, Noto Sans
 */
const normalizeTextForPDF = (text) => {
  if (!text) return '';

  // Ensure text is a string
  const str = String(text);

  // Character mappings to closest ASCII equivalents
  const charMap = {
    // Turkish characters
    'ğ': 'g', 'Ğ': 'G',
    'ü': 'u', 'Ü': 'U',
    'ş': 's', 'Ş': 'S',
    'ı': 'i', 'İ': 'I',
    'ö': 'o', 'Ö': 'O',
    'ç': 'c', 'Ç': 'C',
    // Common special characters
    'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a',
    'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e',
    'ì': 'i', 'í': 'i', 'î': 'i', 'ï': 'i',
    'ò': 'o', 'ó': 'o', 'ô': 'o', 'õ': 'o',
    'ù': 'u', 'ú': 'u', 'û': 'u',
    'ñ': 'n', 'ý': 'y', 'ÿ': 'y',
    // Uppercase variants
    'À': 'A', 'Á': 'A', 'Â': 'A', 'Ã': 'A', 'Ä': 'A',
    'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E',
    'Ì': 'I', 'Í': 'I', 'Î': 'I', 'Ï': 'I',
    'Ò': 'O', 'Ó': 'O', 'Ô': 'O', 'Õ': 'O',
    'Ù': 'U', 'Ú': 'U', 'Û': 'U',
    'Ñ': 'N', 'Ý': 'Y'
  };

  // Replace special characters with closest ASCII equivalents
  return str.replace(/[ğĞüÜşŞıİöÖçÇàáâãäèéêëìíîïòóôõùúûñýÿÀÁÂÃÄÈÉÊËÌÍÎÏÒÓÔÕÙÚÛÑÝ]/g,
    char => charMap[char] || char);
};

/**
 * Fetches brand data to get company name for PDF header
 */
export const fetchBrandCompanyName = async (apiUrl, workspaceId) => {
  try {
    const brandvoxToken = localStorage.getItem('brandvox_token');
    if (!brandvoxToken || !apiUrl || !workspaceId) {
      return null;
    }

    const response = await fetch(`${apiUrl}/api/v1/brand/get-brand?workspace_id=${workspaceId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${brandvoxToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const brandData = await response.json();
      return brandData.company_name || null;
    }
  } catch (error) {
  }
  return null;
};

// BrandVox unified PDF styling configuration - Updated to match chat PDF exactly
export const BRANDVOX_PDF_THEME = {
  colors: {
    primary: [55, 26, 82],        // #371a52 - DARK_BLUE from chat PDF
    secondary: [118, 56, 177],     // #7638b1 - Purple accent
    tertiary: [139, 67, 206],      // #8b43ce - Light purple for alternating rows
    background: [244, 238, 252],   // #f4eefc - Light purple background
    textDark: [33, 37, 41],
    textMuted: [128, 128, 128],
    white: [255, 255, 255],
    lightPurple: [244, 238, 252]   // Light purple background
  },
  fonts: {
    title: { size: 18, weight: 'bold' },
    subtitle: { size: 14, weight: 'bold' },
    normal: { size: 10, weight: 'normal' },
    small: { size: 8, weight: 'normal' }
  },
  spacing: {
    margin: 40,  // Updated to match chat PDF margin
    headerHeight: 80,  // Updated to match chat PDF header height
    footerHeight: 25   // Updated to match chat PDF footer height
  }
};

/**
 * Creates a new PDF document with BrandVox styling with UTF-8 support
 *
 * Note: The default PDF fonts (Helvetica, Times, Courier) have limited support for
 * special characters like Turkish (ğ, ü, ş, ö, ç, ı). For full Turkish character support,
 * you would need to add a custom TrueType font using doc.addFont() method.
 *
 * The current configuration uses compression and proper encoding to improve
 * character display, but some characters may not render perfectly with default fonts.
 *
 * @param {string} orientation - 'p' for portrait (default) or 'l' for landscape
 */
export const createBrandVoxPDF = (orientation = 'p') => {
  // Enable UTF-8 support by using compress option
  const doc = new jsPDF({
    orientation: orientation, // 'p' for portrait, 'l' for landscape
    unit: 'pt',
    format: 'a4',
    putOnlyUsedFonts: true,
    compress: true
  });

  // Set Helvetica as the default font for the document (matching chat PDF)
  // Note: Courier font ('courier') may have slightly better Unicode support
  // but Helvetica is used for visual consistency
  doc.setFont('helvetica', 'normal');

  return doc;
};

/**
 * Adds a branded header to the PDF with logo and enhanced styling - Updated to match chat PDF exactly
 */
export const addBrandVoxHeader = async (doc, title, subtitle = null, agentName = null, companyName = null) => {
  const { colors, spacing } = BRANDVOX_PDF_THEME;
  const pageWidth = doc.internal.pageSize.width;

  // Header background with primary color - exactly 80pt height like chat PDF
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, pageWidth, 80, 'F');

  // Logo positioning - same as chat PDF
  const logoWidth = 60;
  const logoX = spacing.margin;
  const logoY = 10; // 10pt from top like chat PDF

  // Try to add logo from public folder
  try {
    const img = new Image();
    img.src = '/logo512.png'; // Path to logo in public folder

    // Use a promise to properly load the image before adding it
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    const logoHeight = img.height * logoWidth / img.width; // Calculate height proportionally
    doc.addImage(img, 'PNG', logoX, logoY, logoWidth, logoHeight);
  } catch (error) {
    // Fallback if logo fails to load - just continue without logo
  }

  // Title positioning next to logo - same formula as chat PDF
  const titleX = spacing.margin + logoWidth + 15;

  // Main title with exact same formatting as chat PDF
  doc.setFontSize(18); // Exactly match chat PDF font size
  doc.setTextColor(...colors.white);
  doc.setFont('helvetica', 'bold');
  doc.text(normalizeTextForPDF(title), titleX, 35); // Same Y position as chat PDF

  // Add subtitle if provided
  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(normalizeTextForPDF(subtitle), titleX, 55);
  }

  // BrandVox platform branding and company name
  doc.setFontSize(9);
  if (companyName) {
    doc.text(normalizeTextForPDF(`${companyName} • BrandVox AI Platform`), titleX, subtitle ? 70 : 55);
  } else {
    doc.text('BrandVox AI Platform', titleX, subtitle ? 70 : 55);
  }

  return 100; // Return next Y position after header
};

/**
 * Adds a branded footer to the PDF with gradient styling - Updated to match chat PDF exactly
 */
export const addBrandVoxFooter = (doc, pageNumber = 1) => {
  const { colors } = BRANDVOX_PDF_THEME;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Footer background - match chat PDF styling exactly
  doc.setFillColor(...colors.primary); // Dark background
  doc.rect(0, pageHeight - 25, pageWidth, 25, 'F');

  // Branding text on the left - same as chat PDF
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.white);
  doc.setFontSize(8);
  doc.text("Generated with BrandVox AI", 14, pageHeight - 10);

  // Page number in center - same as chat PDF
  doc.setFontSize(8);
  doc.text(`Page ${pageNumber}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
};

/**
 * Creates a styled info box with enhanced BrandVox styling - Updated to match chat PDF exactly
 */
export const createInfoBox = (doc, startY, title, infoData) => {
  const { colors, spacing } = BRANDVOX_PDF_THEME;
  const pageWidth = doc.internal.pageSize.width;
  const detailsWidth = pageWidth - (spacing.margin * 2);
  const rowHeight = 22;  // Reduced from 25 to 22 for tighter spacing

  // Convert infoData to standardized format first to calculate height
  let standardizedData = [];

  if (Array.isArray(infoData)) {
    if (infoData.length > 0) {
      // Check if it's array of arrays format (from ExportDropdown)
      if (Array.isArray(infoData[0])) {
        standardizedData = infoData.map(([label, value], index) => ({
          icon: '•',
          label: String(label || ''),
          value: String(value || '')
        }));
      }
      // Check if it's array of objects format (from chat PDF)
      else if (typeof infoData[0] === 'object' && infoData[0].label !== undefined) {
        standardizedData = infoData.map(item => ({
          icon: String(item.icon || '•'),
          label: String(item.label || ''),
          value: String(item.value || '')
        }));
      }
    }
  }

  // Calculate number of rows (2 items per row)
  const numRows = Math.ceil(standardizedData.length / 2);

  // Calculate total height: Header (20) + top spacing (30) + rows + bottom spacing
  const detailsHeight = 20 + 30 + (numRows * rowHeight);  // Reduced bottom padding to 15pt

  // Create a cleaner white background for the entire details section
  doc.setFillColor(...colors.white);
  doc.roundedRect(spacing.margin, startY, detailsWidth, detailsHeight, 5, 5, 'F');
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.75);
  doc.roundedRect(spacing.margin, startY, detailsWidth, detailsHeight, 5, 5, 'S');

  // Add a styled header bar for the details section - match header/footer color
  doc.setFillColor(...colors.primary); // Match header/footer background color
  doc.rect(spacing.margin, startY, detailsWidth, 20, 'F');

  // Add the section title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...colors.white);  // White text on dark background
  doc.text(normalizeTextForPDF(title), spacing.margin + 10, startY + 13);

  // Create two column layout for information
  const colWidth = detailsWidth / 2;
  const col1X = spacing.margin + 20;
  const col2X = col1X + colWidth;
  const rowStartY = startY + 30;

  // Helper function to add an info item with icon
  const addInfoItem = (x, y, icon, label, value, iconColor = [60, 60, 60]) => {
    // Validate inputs to prevent jsPDF errors and normalize for Turkish characters
    const safeIcon = String(icon || '•');
    const safeLabel = normalizeTextForPDF(String(label || ''));
    const safeValue = normalizeTextForPDF(String(value || ''));

    // Draw icon
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(safeIcon, x, y);

    // Draw label
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(safeLabel, x + 10, y);

    // Draw value
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(safeValue, x + 10, y + 12);
  };

  // Add info items in two columns
  let currentRow = 0;
  for (let i = 0; i < standardizedData.length; i += 2) {
    const item1 = standardizedData[i];
    const item2 = standardizedData[i + 1];

    const yPos = rowStartY + (currentRow * (rowHeight + 5)) + 10;

    // Add first item
    addInfoItem(col1X, yPos, item1.icon, item1.label, item1.value);

    // Add second item if exists
    if (item2) {
      addInfoItem(col2X, yPos, item2.icon, item2.label, item2.value);
    }

    currentRow++;
  }

  // Return the Y position after the box
  return startY + detailsHeight + 10;
};

/**
 * Creates a styled summary table with enhanced BrandVox styling - Updated to match chat PDF exactly
 */
export const createSummaryTable = (doc, startY, summaryData) => {
  return createInfoBox(doc, startY, "Report Summary", summaryData);
};

/**
 * Creates a styled data table with beautiful BrandVox styling - Updated to match chat PDF exactly
 */
export const createDataTable = (doc, startY, headers, data, columnStyles = {}, options = {}) => {
  const { colors, fonts, spacing } = BRANDVOX_PDF_THEME;
  const pageWidth = doc.internal.pageSize.width;
  // pageHeight used for pagination calculations in autoTable

  if (data.length === 0) {
    doc.setFontSize(fonts.subtitle.size - 2);
    doc.setTextColor(...colors.textMuted);
    doc.text('No data found for the selected criteria.', spacing.margin, startY + 10);
    return startY + 30;
  }

  let pageNumber = 1;

  // Calculate available width for the table
  const availableWidth = pageWidth - (spacing.margin * 2);
  const numColumns = headers.length;

  // Create optimized column styles that fit within page boundaries
  const optimizedColumnStyles = {};
  if (numColumns > 0) {
    // Define priority and min widths for different column types
    const columnPriorities = {};
    const minWidths = {};

    // Set priorities (higher = more important) and minimum widths
    headers.forEach((header, index) => {
      switch (header) {
        case 'Date':
          columnPriorities[index] = 8;
          minWidths[index] = 60;
          break;
        case 'Name':
          columnPriorities[index] = 7;
          minWidths[index] = 70;
          break;
        case 'Email':
          columnPriorities[index] = 6;
          minWidths[index] = 80;
          break;
        case 'Phone':
          columnPriorities[index] = 5;
          minWidths[index] = 60;
          break;
        case 'Location':
          columnPriorities[index] = 4;
          minWidths[index] = 70;
          break;
        case 'Source':
          columnPriorities[index] = 3;
          minWidths[index] = 50;
          break;
        case 'Question':
          columnPriorities[index] = 10; // Highest priority for conversations
          minWidths[index] = 100;
          break;
        case 'Answer':
          columnPriorities[index] = 9; // Second highest for conversations
          minWidths[index] = 120;
          break;
        case 'Model':
          columnPriorities[index] = 2;
          minWidths[index] = 50;
          break;
        case 'Country':
          columnPriorities[index] = 3;
          minWidths[index] = 60;
          break;
        default:
          columnPriorities[index] = 1;
          minWidths[index] = 60;
      }
    });

    // Calculate total minimum width needed
    const totalMinWidth = Object.values(minWidths).reduce((sum, width) => sum + width, 0);

    if (totalMinWidth <= availableWidth) {
      // We have enough space, distribute remaining width by priority
      const remainingWidth = availableWidth - totalMinWidth;
      const totalPriority = Object.values(columnPriorities).reduce((sum, priority) => sum + priority, 0);

      headers.forEach((header, index) => {
        const minWidth = minWidths[index];
        const priority = columnPriorities[index];
        const extraWidth = (remainingWidth * priority) / totalPriority;
        optimizedColumnStyles[index] = {
          cellWidth: Math.floor(minWidth + extraWidth)
        };
      });
    } else {
      // Not enough space, use proportional scaling
      const scaleFactor = availableWidth / totalMinWidth;
      headers.forEach((header, index) => {
        optimizedColumnStyles[index] = {
          cellWidth: Math.floor(minWidths[index] * scaleFactor)
        };
      });
    }
  }

  autoTable(doc, {
    startY: startY,
    head: [headers.map(h => normalizeTextForPDF(String(h || '')))],
    body: data.map(row => row.map(cell => normalizeTextForPDF(String(cell || '')))),
    theme: 'grid',
    headStyles: {
      fillColor: colors.primary,  // Match header/footer background color
      textColor: colors.white,
      fontSize: 11,
      font: 'helvetica',
      fontStyle: 'bold',
      cellPadding: 8
    },
    bodyStyles: {
      fontSize: 10,
      textColor: colors.textDark,
      font: 'helvetica',
      cellPadding: 6,
      lineColor: [240, 240, 240],
      lineWidth: 0.2
    },
    alternateRowStyles: {
      fillColor: colors.white  // All rows white, no alternating color
    },
    columnStyles: optimizedColumnStyles, // Use optimized column styles instead of passed ones
    styles: {
      overflow: 'linebreak',
      lineColor: [240, 240, 240],
      lineWidth: 0.2,
      fontSize: 10,
      font: 'helvetica'
    },
    margin: {
      top: 40, // Increased top margin to create space for header on all pages
      bottom: 30,
      left: spacing.margin,
      right: spacing.margin
    },
    tableWidth: 'wrap', // Fit to content within margins
    pageBreak: 'auto',
    rowPageBreak: 'auto',
    didDrawPage: function (data) {
      pageNumber = data.pageNumber;

      // Add mini header to continuation pages (like chat PDF) - matching main header style
      if (data.pageNumber > 1) {
        doc.setFillColor(...colors.primary); // Use primary color like main header
        doc.rect(0, 0, pageWidth, 35, 'F'); // Increased height for more padding

        doc.setTextColor(...colors.white);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(normalizeTextForPDF(options.continuedTitle || "BrandVox AI Report (continued)"), 14, 22); // Adjusted Y position
      }

      // Add footer to all pages
      addBrandVoxFooter(doc, pageNumber);
    }
  });

  return doc.lastAutoTable.finalY;
};

/**
 * Adds a section title - Updated to match chat PDF
 */
export const addSectionTitle = (doc, title, yPos) => {
  const { colors, spacing } = BRANDVOX_PDF_THEME;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...colors.textDark);
  doc.text(normalizeTextForPDF(`» ${title}`), spacing.margin + 10, yPos);

  return yPos + 15;
};

/**
 * Helper function to get a friendly model display name
 * @param {string} modelId - The model ID to look up
 * @param {Function} [resolverFn] - Optional function to resolve model names (from AIModelsContext)
 */
const getModelDisplayName = (modelId, resolverFn) => {
  if (!modelId) return 'Unknown';

  // If a resolver function is provided (from context), use it
  if (resolverFn && typeof resolverFn === 'function') {
    const resolved = resolverFn(modelId);
    if (resolved && resolved !== modelId) {
      return resolved;
    }
  }

  return null;
};

/**
 * Creates a conversation table specifically for chat data - Shows Q&A in separate rows chronologically
 */
export const createConversationTable = (doc, startY, conversations, options = {}) => {
  const { colors } = BRANDVOX_PDF_THEME;
  const pageWidth = doc.internal.pageSize.width;
  // pageHeight used for pagination calculations in autoTable

  // Get session metadata for user info
  const sessionMetadata = options.sessionMetadata || {};
  const leadInfo = sessionMetadata.lead_info || {};
  const userName = normalizeTextForPDF(leadInfo.fullName || leadInfo.name || 'User');

  // Flatten conversations into individual messages (questions and answers separately)
  const messages = [];

  conversations.forEach((chat) => {
    // Add question as separate message if it exists
    if (chat.question && chat.question.trim() !== '') {
      messages.push({
        timestamp: chat.timestamp,
        type: 'Question',
        sender: userName,
        message: normalizeTextForPDF(chat.question),
        feedback: '', // Questions don't have feedback
        source: normalizeTextForPDF(chat.message_source || 'Unknown'),
        country: normalizeTextForPDF(chat.country || 'Unknown'),
        city: normalizeTextForPDF(chat.city || 'Unknown'),
        ip: chat.user_ip || 'N/A'
      });
    }

    // Add answer as separate message if it exists
    if (chat.answer && chat.answer.trim() !== '') {
      let responder = '';

      // Determine responder based on model type
      if (chat.model === 'human') {
        // Human response - show agent name and email
        const agentName = normalizeTextForPDF(chat.agent_display_name || 'Human Agent');
        const agentEmail = normalizeTextForPDF(chat.human_support_email || 'N/A');
        responder = `${agentName} (${agentEmail})`;
      } else {
        // AI response - show model name (use resolver if provided)
        responder = getModelDisplayName(chat.model, options.getModelDisplayName);
      }

      messages.push({
        timestamp: chat.timestamp,
        type: 'Answer',
        sender: responder,
        message: normalizeTextForPDF(chat.answer),
        feedback: chat.liked || 'None',
        source: normalizeTextForPDF(chat.message_source || 'Unknown'),
        country: normalizeTextForPDF(chat.country || 'Unknown'),
        city: normalizeTextForPDF(chat.city || 'Unknown'),
        ip: chat.model === 'human' ? 'Human Agent' : (chat.user_ip || 'N/A')
      });
    }
  });

  // Sort messages chronologically (oldest to newest)
  messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // Group messages by date for better presentation
  const groupedByDate = messages.reduce((groups, msg) => {
    const date = new Date(msg.timestamp).toLocaleDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(msg);
    return groups;
  }, {});

  // Sort dates from oldest to newest
  const sortedDates = Object.keys(groupedByDate).sort((a, b) =>
    new Date(a) - new Date(b)
  );

  // Prepare table data
  const tableData = [];

  // Add each message to tableData
  sortedDates.forEach(date => {
    // Add date header
    tableData.push([
      {
        content: normalizeTextForPDF(`Date: ${date}`),
        colSpan: 9,
        styles: {
          fontStyle: 'bold',
          fillColor: colors.white,
          textColor: colors.textDark,
          fontSize: 11,
          cellPadding: 6,
          font: 'helvetica',
          halign: 'left'
        }
      }
    ]);

    // Add each message under this date
    groupedByDate[date].forEach((msg) => {
      // Format timestamp
      const dateTime = new Date(msg.timestamp);
      const formattedDateTime = `${dateTime.toLocaleDateString()} ${dateTime.toLocaleTimeString()}`;

      // Format message text - normalize Turkish characters
      const messageText = normalizeTextForPDF(
        msg.message.split('\n')
          .filter(line => line.trim() !== '')
          .join('\n')
      );

      // Get feedback color for answers
      let feedbackColor = [128, 128, 128]; // Gray for none/empty
      if (msg.feedback === 'Liked') {
        feedbackColor = [25, 135, 84]; // Success green
      } else if (msg.feedback === 'Disliked') {
        feedbackColor = [220, 53, 69]; // Error red
      }

      // Create row
      tableData.push([
        { content: formattedDateTime, styles: { fontSize: 7, fillColor: colors.white, font: 'helvetica' } },
        { content: msg.type, styles: { fontSize: 8, fillColor: colors.white, font: 'helvetica', fontStyle: 'bold' } },
        { content: msg.sender, styles: { fontSize: 8, fillColor: colors.white, font: 'helvetica' } },
        { content: messageText, styles: { fontSize: 9, fillColor: colors.white, font: 'helvetica', cellPadding: 5 } },
        { content: msg.feedback, styles: { fontSize: 8, textColor: feedbackColor, fillColor: colors.white, font: 'helvetica' } },
        { content: msg.source, styles: { fontSize: 7, fillColor: colors.white, font: 'helvetica' } },
        { content: msg.country, styles: { fontSize: 8, fillColor: colors.white, font: 'helvetica' } },
        { content: msg.city, styles: { fontSize: 8, fillColor: colors.white, font: 'helvetica' } },
        { content: msg.ip, styles: { fontSize: 7, fillColor: colors.white, font: 'helvetica' } }
      ]);
    });
  });

  // Add the table to the PDF
  autoTable(doc, {
    startY: startY,
    head: [[
      { content: 'Date & Time', styles: { halign: 'center' } },
      { content: 'Type', styles: { halign: 'center' } },
      { content: 'Sender/Responder', styles: { halign: 'left' } },
      { content: 'Message', styles: { halign: 'left' } },
      { content: 'Feedback', styles: { halign: 'center' } },
      { content: 'Source', styles: { halign: 'center' } },
      { content: 'Country', styles: { halign: 'center' } },
      { content: 'City', styles: { halign: 'center' } },
      { content: 'IP Address', styles: { halign: 'center' } }
    ]],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: colors.primary,
      textColor: colors.white,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 5,
      font: 'helvetica'
    },
    columnStyles: {
      0: { cellWidth: 65, fontSize: 7 },    // Date & Time
      1: { cellWidth: 50, fontSize: 8 },    // Type
      2: { cellWidth: 100, fontSize: 8 },   // Sender/Responder
      3: { cellWidth: 320, fontSize: 9 },   // Message (widest column)
      4: { cellWidth: 55, fontSize: 8 },    // Feedback
      5: { cellWidth: 50, fontSize: 7 },    // Source
      6: { cellWidth: 60, fontSize: 8 },    // Country
      7: { cellWidth: 60, fontSize: 8 },    // City
      8: { cellWidth: 60, fontSize: 7 }     // IP Address
    },
    styles: {
      overflow: 'linebreak',
      cellPadding: 4,
      lineColor: [240, 240, 240],
      lineWidth: 0.2,
      fontSize: 8,
      font: 'helvetica'
    },
    alternateRowStyles: {
      fillColor: colors.white
    },
    margin: { top: 40, bottom: 30, left: 20, right: 20 },
    pageBreak: 'auto',
    rowPageBreak: 'auto',
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        doc.setFillColor(...colors.primary);
        doc.rect(0, 0, pageWidth, 35, 'F');
        doc.setTextColor(...colors.white);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text("Chat Conversation (continued)", 14, 22);
      }
      addBrandVoxFooter(doc, data.pageNumber);
    }
  });

  return doc.lastAutoTable.finalY;
};

/**
 * Generates a complete BrandVox report - Updated to match chat PDF structure
 */
export const generateBrandVoxReport = async (config) => {
  const {
    title,
    subtitle,
    agentName,
    summaryData,
    headers,
    data,
    conversations,
    columnStyles = {},
    filename,
    appliedFilters = {},
    sessionId = null,
    companyName = null,
    sessionMetadata = null,
    getModelDisplayName: modelNameResolver = null
  } = config;

  // Use landscape orientation for chat exports with many columns
  const orientation = conversations && conversations.length > 0 ? 'l' : 'p';
  const doc = createBrandVoxPDF(orientation);

  // Add header (now async)
  let yPos = await addBrandVoxHeader(doc, title, subtitle, agentName, companyName);

  // Add summary section if provided
  if (summaryData && summaryData.length > 0) {
    yPos = createSummaryTable(doc, yPos, summaryData);
  }

  // Add conversation table if provided (for chat exports)
  if (conversations && conversations.length > 0) {
    createConversationTable(doc, yPos, conversations, {
      sessionId,
      agentName,
      sessionMetadata,
      continuedTitle: "Chat Conversation (continued)",
      getModelDisplayName: modelNameResolver
    });
  }
  // Add data table section if provided
  else if (headers && data) {
    createDataTable(doc, yPos, headers, data, columnStyles, {
      sessionId,
      agentName,
      continuedTitle: "BrandVox AI Report (continued)"
    });
  }

  // Generate filename with filters if provided
  let finalFilename = filename || 'brandvox-report';
  if (sessionId) {
    finalFilename = `BrandVox-Chat-${sessionId.substring(sessionId.length - 8)}`;
  }
  if (appliedFilters.startDate && appliedFilters.endDate) {
    const dateStr = `${appliedFilters.startDate}_to_${appliedFilters.endDate}`;
    finalFilename += `_${dateStr.replace(/-/g, '')}`;
  }

  // Add current date to filename
  const currentDate = new Date().toLocaleString().replace(/[/:\\]/g, '-');
  finalFilename += `-${currentDate}.pdf`;

  // Save the PDF
  doc.save(finalFilename);

  return doc;
};

const pdfUtils = {
  createBrandVoxPDF,
  addBrandVoxHeader,
  addBrandVoxFooter,
  createInfoBox,
  createSummaryTable,
  createDataTable,
  createConversationTable,
  addSectionTitle,
  generateBrandVoxReport,
  BRANDVOX_PDF_THEME
};

export default pdfUtils;